import React from 'react';
import { Button, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { IconCheckmark } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import {
  ChecklistButtonStateText,
  ChecklistCompletionStatus,
  ChecklistOption,
  RFQChecklistCard,
} from 'types/enrollmentService';
import { isFilesStep, RFQ_PROPOSAL_CARD_DATA } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { createStyles } from './styles';

type CardStatusesTitlesObjectType = {
  [K in ChecklistCompletionStatus]: string | JSX.Element;
};

type CardButtonStateObjectType = {
  [K in ChecklistButtonStateText]: string;
};

const BUTTON_STATE_TEXT: CardButtonStateObjectType = Object.freeze({
  VIEW_EDIT: 'View / Edit',
  VIEW: 'View',
  UNAVAILABLE: 'Unavailable',
  CONTINUE: 'Continue',
  REVIEW_SUBMIT: 'Review and Submit',
  PROCESSING: 'Processing',
});

interface ProposalChecklistCardProps {
  option: ChecklistOption<RFQChecklistCard>;
  periodId: string;
  groupId: string;
  defaultProductLineId: ProductLineId;
  isAvailable: boolean;
}

const RFQProposalStageCard = ({
  option,
  periodId,
  defaultProductLineId,
  isAvailable,
  groupId,
}: ProposalChecklistCardProps): JSX.Element => {
  const classes = createStyles();
  const { card, completionStatus, buttonState } = option;

  const history = useHistory();
  const query = useQueryParams();
  const cartId = query.get('cartId') || '';
  const isFilesStepCard = isFilesStep(card);

  const onRedirect = (): void => {
    const cardDataObject = RFQ_PROPOSAL_CARD_DATA[card];
    history.push(cardDataObject.redirectTo(cartId, periodId, groupId, defaultProductLineId));
  };

  const ContinueButton = (
    <Button color="primary" size="large" onClick={onRedirect} className={classes.actionButton}>
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const ViewEditButton = (
    <Button type="opaque" color="primary" size="large" onClick={onRedirect} className={classes.actionButton}>
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const UnavailableButton = (
    <Button size="large" disabled className={classes.actionButton}>
      {BUTTON_STATE_TEXT[buttonState]}
    </Button>
  );

  const renderActionButton = (): JSX.Element | null => {
    switch (buttonState) {
      case ChecklistButtonStateText.UNAVAILABLE:
        return UnavailableButton;
      case ChecklistButtonStateText.VIEW:
      case ChecklistButtonStateText.VIEW_EDIT:
        return ViewEditButton;
      case ChecklistButtonStateText.CONTINUE:
        return ContinueButton;

      default:
        return null;
    }
  };

  const STATUSES_TITLES_OBJECT: CardStatusesTitlesObjectType = Object.freeze({
    COMPLETED: (
      <span className={classes.completedCardWrapper}>
        <IconCheckmark className={classes.icon} width="1.8rem" />
        <span className={classes.cardStatusText}>Completed</span>
      </span>
    ),
    READY_TO_BEGIN: (
      <span className={clsx(classes.cardStatusText, classes.readyToBeginStatusText)}>Ready to begin</span>
    ),
    CANNOT_COMPLETE_YET: <span className={classes.cardStatusText}>Cannot complete yet</span>,
    IN_PROGRESS: <span className={clsx(classes.cardStatusText, classes.inProgressStatusText)}>In-Progress</span>,
  });

  return (
    <div className={clsx(classes.card, isFilesStepCard && classes.filesStepCard)}>
      <div>
        <h3 className={classes.title}>{RFQ_PROPOSAL_CARD_DATA[card].title}</h3>
        <p className={classes.text}>{RFQ_PROPOSAL_CARD_DATA[card].text}</p>
      </div>
      <div className={classes.rightSection}>
        {isAvailable ? (
          <>
            {renderActionButton()}
            {STATUSES_TITLES_OBJECT[completionStatus]}
          </>
        ) : (
          <>
            <Button size="large" disabled className={classes.actionButton}>
              Internal Use Only
            </Button>
            <span className={classes.notAvailableCardWrapper}>{STATUSES_TITLES_OBJECT[completionStatus]}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default RFQProposalStageCard;
